import { apiClient } from "../index";

const USERS_ORGANIZATIONS_PATH =
  "/api/v1/dashboard/platform-admin/users-organizations";
const PLATFORM_ANALYTICS_PATH =
  "/api/v1/dashboard/platform-admin/platform-analytics";
const INFRASTRUCTURE_PATH = "/api/v1/dashboard/platform-admin/infrastructure";
const TOPOLOGY_USAGE_PATH = "/api/v1/dashboard/platform-admin/topology-usage";
const ACTIVITY_DEPLOYMENTS_PATH =
  "/api/v1/dashboard/platform-admin/activity-deployments";
const PENDING_APPROVALS_PATH =
  "/api/v1/dashboard/platform-admin/pending-approvals";
const TEAM_CHALLENGES_PATH = "/api/v1/dashboard/platform-admin/team-challenges";
const TOP_PERFORMING_COMPANIES_PATH =
  "/api/v1/dashboard/platform-admin/top-performing-companies";

/**
 * Row 1 — User Roles & Organizations.
 */
export async function getUsersOrganizations() {
  return apiClient.get(USERS_ORGANIZATIONS_PATH);
}

/**
 * Normalize the raw API payload into the flat shape the dashboard cards expect.
 * Defensive about missing fields so the UI never crashes on partial data.
 */
export function normalizeUsersOrganizations(raw) {
  const compData = raw?.companies?.chart?.datasets?.[0]?.data ?? [];
  const uniData = raw?.universities?.chart?.datasets?.[0]?.data ?? [];

  return {
    companies: {
      total: raw?.companies?.total ?? 0,
      users: compData[0] ?? 0,
      admins: compData[1] ?? 0,
    },
    universities: {
      total: raw?.universities?.total ?? 0,
      students: uniData[0] ?? 0,
      admins: uniData[1] ?? 0,
    },
    students: raw?.individual_students ?? 0,
    professionals: raw?.professionals ?? 0,
  };
}

/**
 * Row 2 — Platform Analytics (30-day new-user trend, excludes platform_admin).
 */

export async function getPlatformAnalytics() {
  return apiClient.get(PLATFORM_ANALYTICS_PATH);
}

/**
 * Normalize platform-analytics into the shape the trend chart expects.
 * Formats ISO labels into "Mon D" for the x-axis.
 */
export function normalizePlatformAnalytics(raw) {
  const labels = raw?.signups_chart?.labels ?? [];
  const series = raw?.signups_chart?.datasets?.[0]?.data ?? [];

  return {
    newUsersToday: raw?.new_users_today ?? 0,
    newUsersLast30d: raw?.new_users_last_30d ?? 0,
    labels: labels.map((iso) => {
      const d = new Date(iso + "T00:00:00"); // avoid TZ shifting the date
      return d.toLocaleDateString("en", { month: "short", day: "numeric" });
    }),
    series,
  };
}

/**
 * Row 3 — Infrastructure (total boxes, with-topology, total topologies).
 */
export async function getInfrastructure() {
  return apiClient.get(INFRASTRUCTURE_PATH);
}

/**
 * Normalize infrastructure data. Coverage % is derived (not in payload):
 * round(boxes_with_topology / total_boxes * 100).
 */
export function normalizeInfrastructure(raw) {
  const totalBoxes = raw?.total_boxes ?? 0;
  const withTopologies = raw?.boxes_with_topology ?? 0;
  const totalTopologies = raw?.total_topologies ?? 0;
  const coverage =
    totalBoxes > 0 ? Math.round((withTopologies / totalBoxes) * 100) : 0;

  return {
    totalBoxes,
    withTopologies,
    totalTopologies,
    coverage,
    unassigned: Math.max(totalBoxes - withTopologies, 0),
  };
}

/**
 * Row 4 — Topology Usage Statistics (per-org topology + scenarios-with-topology).
 */
export async function getTopologyUsage(limit = 5) {
  return apiClient.get(`${TOPOLOGY_USAGE_PATH}?limit=${limit}`);
}

/** Build up-to-2-char initials from an org name. */
function initialsFromName(name = "") {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Normalize topology-usage rows for the table.
 * initials are derived from org_name; tint cycles a fixed palette by index.
 */
export function normalizeTopologyUsage(raw) {
  const TINT_KEYS = ["teal", "blue", "violet", "orange", "red"];
  const items = raw?.items ?? [];

  return items.map((item, i) => ({
    id: item.org_id,
    name: item.org_name ?? "Unknown",
    type: item.org_type ?? "",
    topologies: item.topologies_count ?? 0,
    boxes: item.scenarios_with_topology ?? 0,
    initials: initialsFromName(item.org_name),
    tint: TINT_KEYS[i % TINT_KEYS.length],
  }));
}

/**
 * Row 5 — Activity & Deployments.
 */
export async function getActivityDeployments(topOrgsLimit = 5) {
  return apiClient.get(
    `${ACTIVITY_DEPLOYMENTS_PATH}?top_orgs_limit=${topOrgsLimit}`,
  );
}

export function normalizeActivityDeployments(raw) {
  const daily = raw?.daily_runs_chart ?? {};
  const dailyLabels = daily.labels ?? [];
  const dailyDatasets = daily.datasets ?? [];
  const completedSeries =
    dailyDatasets.find((d) => d.label === "Completed")?.data ??
    dailyDatasets[0]?.data ??
    [];
  const failedSeries =
    dailyDatasets.find((d) => d.label === "Failed")?.data ??
    dailyDatasets[1]?.data ??
    [];

  const summary = raw?.lifetime_summary ?? {};
  const topOrgs = raw?.top_orgs_chart ?? {};

  return {
    daily: {
      labels: dailyLabels.map((iso) => {
        const d = new Date(iso + "T00:00:00");
        return d.toLocaleDateString("en", { month: "short", day: "numeric" });
      }),
      completed: completedSeries,
      failed: failedSeries,
    },
    summary: {
      requested: summary.requested ?? 0,
      completed: summary.completed ?? 0,
      failed: summary.failed ?? 0,
      successRate: Math.round(summary.success_rate_pct ?? 0),
    },
    topOrgs: {
      labels: topOrgs.labels ?? [],
      values: topOrgs.datasets?.[0]?.data ?? [],
    },
  };
}

/**
 * Row 6 — Pending Approvals (scenarios awaiting platform-admin approval).
 */
export async function getPendingApprovals(limit = 20) {
  return apiClient.get(`${PENDING_APPROVALS_PATH}?limit=${limit}`);
}

export function normalizePendingApprovals(raw) {
  const items = raw?.items ?? [];
  return {
    total: raw?.total ?? items.length,
    items: items.map((it) => {
      const seconds = it.queue_seconds ?? 0;
      const days = Math.floor(seconds / 86400);
      return {
        id: it.scenario_id,
        company: it.org_name ?? "Individual",
        box: it.scenario_name ?? "Untitled",
        days,
        queueLabel: formatQueueDuration(seconds),
      };
    }),
  };
}

/** Turn queue_seconds into a compact human label: "3d", "5h", "12m", "<1m". */
function formatQueueDuration(seconds) {
  if (seconds >= 86400) {
    const d = Math.floor(seconds / 86400);
    return `${d} ${d === 1 ? "day" : "days"}`;
  }
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    return `${h} ${h === 1 ? "hour" : "hours"}`;
  }
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    return `${m} ${m === 1 ? "min" : "mins"}`;
  }
  return "<1 min";
}

/**
 * Row 7 — Team Challenges (teams total / new-30d + active-runs breakdown).
 */
export async function getTeamChallenges() {
  return apiClient.get(TEAM_CHALLENGES_PATH);
}

export function normalizeTeamChallenges(raw) {
  const ar = raw?.active_runs ?? {};
  return {
    totalTeams: raw?.total_teams ?? 0,
    newThisMonth: raw?.new_teams_30d ?? 0,
    activeRuns: ar.total ?? 0,
    individual: ar.individual ?? 0,
    team: ar.team ?? 0,
  };
}

/**
 * Row 8 — Top Performing Companies (share-by-difficulty + overall coverage).
 */
export async function getTopPerformingCompanies(limit = 5) {
  return apiClient.get(`${TOP_PERFORMING_COMPANIES_PATH}?limit=${limit}`);
}

export function normalizeTopPerformingCompanies(raw) {
  const items = raw?.items ?? [];

  return items.map((it, i) => {
    // Accept several plausible field names; fall back to 0 / derived.
    const easyPct =
      it.easy_pct ?? it.easy ?? it.easyShare ?? it.easy_percentage ?? 0;
    const mediumPct =
      it.medium_pct ?? it.medium ?? it.mediumShare ?? it.medium_percentage ?? 0;
    const hardPct =
      it.hard_pct ?? it.hard ?? it.hardShare ?? it.hard_percentage ?? 0;
    const overall =
      it.overall_pct ?? it.overall ?? it.coverage_pct ?? it.coverage ?? 0;

    return {
      id: it.org_id ?? it.id ?? `${it.org_name ?? "org"}-${i}`,
      rank: it.rank ?? i + 1,
      company: it.org_name ?? it.name ?? it.company ?? "Unknown",
      eShare: easyPct,
      mShare: mediumPct,
      hShare: hardPct,
      pct: Math.round(overall),
      unknownCount: it.unknown_count ?? 0,
    };
  });
}
